import React, { useState } from 'react';
import { ChevronUp, ChevronDown, MoreVertical, Eye, Pencil, Copy, Trash2, FileX, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../ui/dropdown-menu';
import { Button } from '../../ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguageNavigation } from '../../routing/LanguageWrapper';
import { ROLES } from '../../../utils/rbac';
import { formatRelativeTime } from '../../../utils/dateUtils';
import './ReportsTable.css';

const STATUS_COLORS = {
  draft: { bg: 'rgba(138, 122, 74, 0.12)', color: '#8A7A4A' },
  in_progress: { bg: 'rgba(74, 90, 58, 0.12)', color: '#4A5A3A' },
  needs_review: { bg: 'rgba(138, 122, 74, 0.12)', color: '#8A7A4A' },
  completed: { bg: 'rgba(90, 122, 58, 0.12)', color: '#5A7A3A' },
  locked: { bg: 'rgba(74, 74, 74, 0.12)', color: '#4A4A4A' },
};

const STATUS_LABELS = {
  draft: 'reports.status.draft',
  in_progress: 'reports.status.in_progress',
  needs_review: 'reports.status.needs_review',
  completed: 'reports.status.completed',
  locked: 'reports.status.locked',
};

export function ReportsTable({ reports, onDelete, onDuplicate, loading, onCreateNew }) {
  const { t, i18n } = useTranslation();
  const { navigateTo } = useLanguageNavigation();
  const { user } = useAuth();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const canCreate = [ROLES.ADMIN, ROLES.REVIEWER].includes(user?.role);
  const canEdit = [ROLES.ADMIN, ROLES.REVIEWER].includes(user?.role);
  const canDelete = user?.role === ROLES.ADMIN || user?.role === ROLES.SUPERADMIN;

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedReports = React.useMemo(() => {
    if (!sortConfig.key) return reports;

    return [...reports].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.key) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'year':
          aVal = a.year;
          bVal = b.year;
          break;
        case 'lastModified':
          aVal = new Date(a.lastModified).getTime();
          bVal = new Date(b.lastModified).getTime();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [reports, sortConfig]);

  const SortableHeader = ({ columnKey, children, className = '' }) => {
    const isSorted = sortConfig.key === columnKey;
    const direction = isSorted ? sortConfig.direction : null;

    return (
      <div
        className={`reports-table__sortable-header ${className}`.trim()}
        onClick={() => handleSort(columnKey)}
      >
        {children}
        {isSorted && (
          direction === 'asc' ? (
            <ChevronUp size={12} />
          ) : (
            <ChevronDown size={12} />
          )
        )}
      </div>
    );
  };

  // eslint-disable-next-line react/prop-types
  SortableHeader.propTypes = {};

  const handleView = (reportId) => {
    navigateTo(`/reports/${reportId}`);
  };

  const handleEdit = (reportId) => {
    if (canEdit) {
      navigateTo(`/reports/${reportId}`);
    }
  };

  const handleDuplicate = (report) => {
    if (canCreate) {
      onDuplicate(report);
    }
  };

  const handleDelete = (report) => {
    if (canDelete) {
      onDelete(report);
    }
  };

  if (loading) {
    return (
      <div className="reports-table">
        <div className="reports-table__loading">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="reports-table__skeleton-row">
              <div className="reports-table__skeleton-cell" />
              <div className="reports-table__skeleton-cell" />
              <div className="reports-table__skeleton-cell" />
              <div className="reports-table__skeleton-cell" />
              <div className="reports-table__skeleton-cell" />
              <div className="reports-table__skeleton-cell" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (sortedReports.length === 0) {
    return (
      <div className="reports-table">
        <div className="reports-table__empty">
          <FileX size={64} className="reports-table__empty-icon" />
          <h3 className="reports-table__empty-title">{t('reports.hub.table.empty.title')}</h3>
          <p className="reports-table__empty-message">{t('reports.hub.table.empty.message')}</p>
          {canCreate && onCreateNew && (
            <Button
              type="button"
              variant="primary"
              icon={<Plus size={18} />}
              onClick={onCreateNew}
              className="reports-table__empty-button"
            >
              {t('reports.hub.actions.createNew')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="reports-table">
      <div className="reports-table__container">
        <div className="reports-table__header">
          <SortableHeader columnKey="name" className="reports-table__header-cell reports-table__header-cell--name">
            {t('reports.hub.table.columns.name')}
          </SortableHeader>
          <SortableHeader columnKey="year" className="reports-table__header-cell reports-table__header-cell--year">
            {t('reports.hub.table.columns.year')}
          </SortableHeader>
          <div className="reports-table__header-cell reports-table__header-cell--status">
            {t('reports.hub.table.columns.status')}
          </div>
          <div className="reports-table__header-cell reports-table__header-cell--progress">
            {t('reports.hub.table.columns.progress')}
          </div>
          <SortableHeader columnKey="lastModified" className="reports-table__header-cell reports-table__header-cell--date">
            {t('reports.hub.table.columns.lastModified')}
          </SortableHeader>
          <div className="reports-table__header-cell reports-table__header-cell--actions">
            {t('reports.hub.table.columns.actions')}
          </div>
        </div>

        {sortedReports.map((report) => {
          const statusStyle = STATUS_COLORS[report.status] || STATUS_COLORS.draft;
          
          return (
            <div key={report.id} className="reports-table__row">
              <div
                className="reports-table__cell reports-table__cell--name"
                onClick={() => handleView(report.id)}
              >
                {report.name}
              </div>
              <div className="reports-table__cell reports-table__cell--year">
                {report.year}
              </div>
              <div className="reports-table__cell reports-table__cell--status">
                <span
                  className="reports-table__status-badge"
                  style={{
                    background: statusStyle.bg,
                    color: statusStyle.color,
                  }}
                >
                  {STATUS_LABELS[report.status] ? t(STATUS_LABELS[report.status]) : report.status}
                </span>
              </div>
              <div className="reports-table__cell reports-table__cell--progress">
                <div className="reports-table__progress-container">
                  <div className="reports-table__progress-bar">
                    <div
                      className="reports-table__progress-fill"
                      style={{ width: `${report.progress}%` }}
                    />
                  </div>
                  <span className="reports-table__progress-text">{report.progress}%</span>
                </div>
              </div>
              <div className="reports-table__cell reports-table__cell--date">
                {formatRelativeTime(new Date(report.lastModified), i18n.resolvedLanguage || i18n.language, t)}
              </div>
              <div className="reports-table__cell reports-table__cell--actions">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="reports-table__actions-trigger">
                      <MoreVertical size={18} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="reports-table__actions-menu">
                    <DropdownMenuItem onClick={() => handleView(report.id)}>
                      <Eye size={16} />
                      {t('common.view')}
                    </DropdownMenuItem>
                    {canEdit && (
                      <DropdownMenuItem onClick={() => handleEdit(report.id)}>
                        <Pencil size={16} />
                        {t('common.edit')}
                      </DropdownMenuItem>
                    )}
                    {canCreate && (
                      <DropdownMenuItem onClick={() => handleDuplicate(report)}>
                        <Copy size={16} />
                        {t('reports.hub.actions.duplicate')}
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="reports-table__actions-item--danger"
                          onClick={() => handleDelete(report)}
                        >
                          <Trash2 size={16} />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
